import { EntityLocationDTO } from '@user/store/map/typings'

/** The fields that identify a pin across a tile refresh. */
export type LocationIdentity = Pick<
  EntityLocationDTO,
  | 'pinId'
  | 'contactLocationId'
  | 'knockStatusLocationId'
  | 'entityId'
  | 'entityType'
  | 'serviceLocationId'
>

/**
 * Determines whether two locations are the same pin.
 *
 * Priority: entityId + entityType (+ serviceLocationId) → contactLocationId →
 * knockStatusLocationId → pinId. Stable identifiers come first because pinId
 * regenerates when tiles are refreshed, so a clicked pin still matches itself
 * after filters change and tiles are re-fetched.
 */
export function isSameLocationIdentity(
  a: LocationIdentity,
  b: LocationIdentity,
): boolean {
  if (
    a.entityId > 0 &&
    b.entityId > 0 &&
    a.entityId === b.entityId &&
    a.entityType &&
    b.entityType &&
    a.entityType.toLowerCase() === b.entityType.toLowerCase()
  ) {
    if (!a.serviceLocationId || !b.serviceLocationId) return true
    return a.serviceLocationId === b.serviceLocationId
  }

  if (
    a.contactLocationId &&
    b.contactLocationId &&
    a.contactLocationId === b.contactLocationId
  ) {
    return true
  }

  // Entity-type agreement is required: a knock-status-only pin and the Lead /
  // Opportunity / PestPac pin created at the same address share a
  // KnockStatusLocationId but are two different pins (SH-1091).
  if (
    a.knockStatusLocationId &&
    b.knockStatusLocationId &&
    a.knockStatusLocationId === b.knockStatusLocationId
  ) {
    if (
      (!a.entityType && !b.entityType) ||
      a.entityType?.toLowerCase() === b.entityType?.toLowerCase()
    ) {
      return true
    }
  }

  if (a.pinId && b.pinId && a.pinId === b.pinId) return true

  return false
}
